import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Send, Loader2, FileText, Paperclip, AlertCircle, Clock, Lock, Download, Printer, X, Eye, EyeOff, Zap, Plus } from 'lucide-react';
import { api } from '../context/AuthContext';
import { MessageBubble } from './MessageBubble';
import { exportChatHtml, printChat, downloadServerPdf, downloadAttachment, fetchAttachmentUrl } from '../lib/chatExport';
import { FilePreviewModal, isPreviewable } from './FilePreviewModal';
import { QuickReplies } from './QuickReplies';
import { Schnellantworten } from './chat/Schnellantworten';
import { RechnungSenden } from './chat/RechnungSenden';
import { MoveMessageDialog } from './MoveMessageDialog';
import { CobrowseEinladenKnopf } from './cobrowse/CobrowseEinladenKnopf';
import { SprachAufnahme } from './chat/Sprachnachricht';
import { VertraulichFilter, VertraulichComposer, passtSicht } from './chat/Vertraulich';
import { NachrichtFreigabe } from './chat/NachrichtFreigabe';

const fmtDateTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const fmtDay = (iso) => {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Heute';
  if (d.toDateString() === yesterday.toDateString()) return 'Gestern';
  return d.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: 'long', year: 'numeric' });
};

const ToolBtn = ({ onClick, disabled, testId, title, children, primary, team }) => (team ? (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testId} aria-label={title} data-tipp={title}
    className={`inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors disabled:opacity-40 [&>svg]:w-4 [&>svg]:h-4 ${primary ? 'text-[#1B3A5C] hover:bg-[#1B3A5C]/10' : 'text-[#7a8a99] hover:text-[#1B3A5C] hover:bg-[#f5f8fa]'}`}>
    {React.Children.toArray(children).filter((k) => React.isValidElement(k))}
  </button>) : (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testId} title={title}
    className="inline-flex items-center gap-1 px-2 py-1 text-[10px] tracking-wider rounded-sm transition-colors disabled:opacity-40 text-[#7a8a99] hover:text-[#1B3A5C] hover:bg-[#f5f8fa]">
    {children}
  </button>)
);

export const Thread = ({ caseId, mandantenNr, currentUser, isTeam, chatEnabled = true, chatRef, fill = false, platzhalter, onGesendet, schnellantworten = false }) => {
  const { search } = useLocation();
  const fokusId = new URLSearchParams(search).get('nachricht');
  const allView = !!mandantenNr;
  const [data, setData] = useState({ messages: [], events: [], threads: [] });
  const [target, setTarget] = useState(caseId || (mandantenNr ? `general_${mandantenNr}` : ''));
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [ziehen, setZiehen] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [eventText, setEventText] = useState('');
  const [showEvent, setShowEvent] = useState(false);
  const [preview, setPreview] = useState(null);
  const [showHidden, setShowHidden] = useState(false);
  const [moving, setMoving] = useState(null);
  const [vertraulich, setVertraulich] = useState('');
  const [extras, setExtras] = useState(false);
  const [plus, setPlus] = useState(false);
  const [aufnahme, setAufnahme] = useState(false);
  const [sicht, setSicht] = useState('alle');
  const [freigabe, setFreigabe] = useState(null);
  const requestId = useRef(null);
  const toggleUngelesen = async (m) => {
    setData((d) => ({ ...d, messages: d.messages.map((x) => (x.id === m.id ? { ...x, manuell_ungelesen: !m.manuell_ungelesen } : x)) }));
    try { await api.post(`/cases/messages/${m.id}/${m.manuell_ungelesen ? 'gelesen' : 'ungelesen'}`); } catch (e) { setError(e?.response?.data?.detail || 'Markierung fehlgeschlagen'); load(true); }
  };
  const insertText = (t) => { setText((cur) => (cur.trim() ? `${cur}\n${t}` : t)); setTimeout(() => taRef.current?.focus(), 0); };
  const endRef = useRef(null);
  const taRef = useRef(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const base = allView ? `/cases/mandanten/${mandantenNr}/messages` : `/cases/${caseId}/messages`;
      const r = await api.get(base, { params: { include_hidden: showHidden || undefined, poll: silent || undefined } });
      setData({ threads: [], hidden_count: 0, ...r.data });
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [caseId, mandantenNr, allView, showHidden]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const id = setInterval(() => { if (!document.hidden) load(true); }, 20000); return () => clearInterval(id); }, [load]);
  const lastId = data.messages[data.messages.length - 1]?.id;
  useEffect(() => {
    const box = endRef.current?.parentElement;
    if (!box) return;
    const msg = fokusId && box.querySelector(`[data-testid="msg-${CSS.escape(fokusId)}"]`);
    if (msg) box.scrollTop += msg.getBoundingClientRect().top - box.getBoundingClientRect().top - 50;
    else if (box.scrollHeight > box.clientHeight) box.scrollTop = box.scrollHeight;
  }, [lastId, fokusId]);
  useEffect(() => {
    const ta = taRef.current; if (!ta) return;
    ta.style.height = 'auto'; ta.style.height = `${Math.min(ta.scrollHeight, 140)}px`;
  }, [text]);

  const sendenFertig = async (draft, punkte = [], ziel = '') => {
    setSending(true); setError('');
    try {
      const fd = new FormData();
      fd.append('text', draft.t);
      fd.append('client_request_id', draft.requestId);
      if (draft.vertraulich) fd.append('vertraulich', draft.vertraulich);
      if (draft.f) fd.append('attachment', draft.f);
      if (punkte.length) { fd.append('unterlagen', JSON.stringify(punkte)); fd.append('unterlagen_bestaetigt', 'true'); fd.append('unterlagen_case_id', ziel); }
      await api.post(`/cases/${draft.target}/messages`, fd);
      if (!draft.direkt) { setText(''); setFile(null); setVertraulich(''); }
      requestId.current = null; setFreigabe(null);
      await load(true);
      window.dispatchEvent(new Event('unterlagen-geaendert'));
      onGesendet?.(draft.t || (draft.f ? 'Anhang' : ''));
    } finally { setSending(false); }
  };
  const send = async (direkt) => {
    if (sending || freigabe) return;
    const t = typeof direkt === 'string' ? direkt : text;
    const f = typeof direkt === 'string' ? null : file;
    if (!t.trim() && !f) return;
    const signature = JSON.stringify([t, f?.name, f?.lastModified, target, vertraulich]);
    if (requestId.current?.signature !== signature) requestId.current = { signature, id: crypto.randomUUID() };
    const draft = { t, f, target, direkt: typeof direkt === 'string', vertraulich: !allView && typeof direkt !== 'string' ? vertraulich : '', requestId: requestId.current.id };
    if (isTeam && ['admin', 'kanzlei_mitarbeiter'].includes(currentUser?.role)) { setFreigabe(draft); return; }
    try {
      await sendenFertig(draft);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Senden fehlgeschlagen');
    } finally { setSending(false); }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const addEvent = async () => {
    if (!eventText.trim()) return;
    try {
      await api.post(`/cases/${target}/events`, { type: 'status', text: eventText });
      setEventText(''); setShowEvent(false);
      await load(true);
    } catch (e) { setError(e?.response?.data?.detail || 'Fehler'); }
  };

  const patchLocal = (id, patch) => setData((prev) => ({ ...prev, messages: prev.messages.map((m) => (m.id === id ? { ...m, ...patch } : m)) }));

  const toggleStar = async (msg) => {
    if (!msg?.id) return;
    const next = !msg.starred;
    patchLocal(msg.id, { starred: next });
    try { await api.patch(`/cases/${msg.case_id || caseId}/messages/${msg.id}`, { starred: next }); }
    catch (e) { patchLocal(msg.id, { starred: !next }); setError(e?.response?.data?.detail || 'Stern konnte nicht geändert werden'); }
  };

  const deleteMessage = async (messageId) => {
    const msg = data.messages.find((m) => m.id === messageId);
    setData((prev) => ({ ...prev, messages: prev.messages.filter((m) => m.id !== messageId) }));
    try { await api.delete(`/cases/${msg?.case_id || caseId}/messages/${messageId}`); }
    catch (e) { setError(e?.response?.data?.detail || 'Nachricht konnte nicht gelöscht werden'); load(true); }
  };

  const hideMessage = async (msg) => {
    setData((prev) => ({ ...prev, hidden_count: (prev.hidden_count || 0) + 1, messages: showHidden ? prev.messages.map((m) => (m.id === msg.id ? { ...m, hidden: true } : m)) : prev.messages.filter((m) => m.id !== msg.id) }));
    try { await api.post(`/cases/${msg.case_id || caseId}/messages/${msg.id}/hide`); }
    catch (e) { setError(e?.response?.data?.detail || 'Nachricht konnte nicht ausgeblendet werden'); load(true); }
  };
  const unhideMessage = async (msg) => {
    setData((prev) => ({ ...prev, hidden_count: Math.max(0, (prev.hidden_count || 0) - 1), messages: prev.messages.map((m) => (m.id === msg.id ? { ...m, hidden: false } : m)) }));
    try { await api.delete(`/cases/${msg.case_id || caseId}/messages/${msg.id}/hide`); }
    catch (e) { setError(e?.response?.data?.detail || 'Nachricht konnte nicht eingeblendet werden'); load(true); }
  };

  const onDownload = (att, cid = caseId) => downloadAttachment(cid, att).catch((e) => setError(e?.response?.data?.detail || 'Datei konnte nicht heruntergeladen werden'));
  const onPreview = async (att, cid = caseId) => {
    if (!isPreviewable(att.mime)) return onDownload(att, cid);
    setPreview({ att, cid, url: '' });
    try { setPreview({ att, cid, url: await fetchAttachmentUrl(cid, att) }); }
    catch (e) { setPreview(null); setError(e?.response?.data?.detail || 'Vorschau konnte nicht geladen werden'); }
  };
  const closePreview = () => { if (preview?.url) URL.revokeObjectURL(preview.url); setPreview(null); };
  const loadThumb = useCallback((att, cid) => fetchAttachmentUrl(cid || caseId, att), [caseId]);
  const onPdf = () => downloadServerPdf(caseId, chatRef || caseId).catch(() => setError('PDF-Export fehlgeschlagen.'));
  const onPrint = () => { const err = printChat(timeline, chatRef || caseId); if (err) setError(err); };

  const timeline = [
    ...data.messages.filter((m) => passtSicht(m, sicht)).map((m) => ({ ...m, _kind: 'msg' })),
    ...(sicht === 'alle' ? data.events : []).map((e) => ({ ...e, _kind: 'evt' })),
  ].sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));

  if (loading) return <div className={`flex items-center justify-center gap-2 text-sm text-[#34536b] ${fill ? 'flex-1' : 'py-12'}`}><Loader2 className="w-4 h-4 animate-spin" /> Lade Nachrichten…</div>;

  const locked = !isTeam && !chatEnabled;
  let lastDay = null;

  const darfAnhang = chatEnabled && !allView;
  const dropZiel = darfAnhang ? {
    'data-eigene-ablage': '1',
    onDragOver: (e) => { if ([...(e.dataTransfer?.types || [])].includes('Files')) { e.preventDefault(); setZiehen(true); } },
    onDragLeave: (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setZiehen(false); },
    onDrop: (e) => { const f = e.dataTransfer?.files?.[0]; setZiehen(false); if (!f) return; e.preventDefault(); setFile(f); },
  } : {};
  return (
    <div className={`${fill ? 'flex flex-col flex-1 min-h-0' : 'flex flex-col'} relative`} data-testid="thread-root" {...dropZiel}>
      {ziehen && <div className="absolute inset-0 z-20 bg-[#1B3A5C]/85 text-white flex items-center justify-center text-sm font-semibold pointer-events-none" data-testid="thread-drop-hinweis">Datei hier loslassen – sie wird an die Nachricht angehängt</div>}
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-1.5 border-b border-gray-100 bg-white">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] tracking-[0.2em] text-[#7a8a99]" data-testid="thread-counter">{timeline.length} {timeline.length === 1 ? 'EINTRAG' : 'EINTRÄGE'}</span>
          {(data.hidden_count > 0 || showHidden) && (
            <button type="button" onClick={() => setShowHidden((v) => !v)} data-testid="toggle-hidden-messages" aria-pressed={showHidden}
              className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] tracking-wider rounded-full border transition-colors ${showHidden ? 'bg-[#1B3A5C] text-white border-[#1B3A5C]' : 'text-[#7a8a99] border-gray-200 hover:text-[#1B3A5C]'}`}>
              {showHidden ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />} {data.hidden_count} AUSGEBLENDET{showHidden ? ' · VERBERGEN' : ' · ANZEIGEN'}
            </button>
          )}
          {isTeam && !allView && caseId && !fill && <CobrowseEinladenKnopf caseId={caseId} />}
          {isTeam && !allView && data.beteiligte?.length > 0 && <VertraulichFilter beteiligte={data.beteiligte} value={sicht} onChange={setSicht} />}
        </div>
        <div className="flex items-center">
          <ToolBtn team={isTeam} onClick={() => exportChatHtml(timeline, chatRef || caseId)} disabled={!timeline.length} testId="export-chat-html" title="Chat als HTML herunterladen"><Download className="w-3 h-3" /> HTML</ToolBtn>
          {!allView && isTeam && <ToolBtn team={isTeam} onClick={onPdf} disabled={!timeline.length} testId="export-chat-pdf-server" title="Chat als PDF herunterladen" primary><FileText className="w-3 h-3" /> PDF</ToolBtn>}
          <ToolBtn team={isTeam} onClick={onPrint} disabled={!timeline.length} testId="export-chat-pdf" title="Drucken / Als PDF speichern"><Printer className="w-3 h-3" /> DRUCKEN</ToolBtn>
        </div>
      </div>

      <div className={`px-3 md:px-5 py-4 overflow-y-auto group/msg ${fill ? 'flex-1 min-h-0' : 'max-h-[60vh]'}`} data-testid="case-thread"
        style={{ background: 'linear-gradient(180deg, #f6f1e8 0%, #efe8db 100%)' }}>
        {timeline.length === 0 && <p className="text-center text-[#34536b]/70 py-10 text-sm">Noch keine Nachrichten. Schreiben Sie die erste Nachricht unten.</p>}
        {timeline.map((item, idx) => {
          const day = item.created_at ? fmtDay(item.created_at) : null;
          const sep = day && day !== lastDay; lastDay = day || lastDay;
          return (
            <React.Fragment key={item.id || idx}>
              {sep && <div className="flex justify-center my-3"><span className="text-[10px] tracking-wider uppercase text-[#34536b]/70 bg-white/80 px-3 py-1 rounded-full shadow-sm">{day}</span></div>}
              {item._kind === 'evt' ? (
                <div className="my-3 text-center" data-testid={`event-${item.id}`}>
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border-l-2 border-[#b8893d] text-xs text-[#34536b]">
                    <Clock className="w-3 h-3 text-[#b8893d]" />
                    <span>{allView && item.thread_label && <span className="font-mono text-[#1B3A5C]/70 mr-1">[{item.thread_label}]</span>}<strong className="text-[#1B3A5C]">{item.text}</strong> · {item.author_name} · {fmtDateTime(item.created_at)}</span>
                  </div>
                </div>
              ) : (
                <MessageBubble item={{ ...item, _highlight: item.id === fokusId }} currentUserId={currentUser?.user_id} isTeam={isTeam} isAdmin={['admin', 'kanzlei_mitarbeiter'].includes(currentUser?.role)} label={allView ? item.thread_label : null}
                  onToggleStar={toggleStar} onDownloadAttachment={(att) => onDownload(att, item.case_id)} onPreviewAttachment={(att) => onPreview(att, item.case_id)}
                  onLoadThumb={(att) => loadThumb(att, item.case_id)} onDelete={deleteMessage} onHide={hideMessage} onUnhide={unhideMessage} onMove={isTeam ? setMoving : undefined} onToggleUngelesen={isTeam ? toggleUngelesen : undefined} />
              )}
            </React.Fragment>
          );
        })}
        <div ref={endRef} />
      </div>

      {error && (
        <div className="flex items-center justify-between gap-2 text-red-700 bg-red-50 border-t border-red-200 px-3 py-2 text-xs" data-testid="thread-error">
          <span className="flex items-center gap-2"><AlertCircle className="w-4 h-4" /> {error}</span>
          <button type="button" onClick={() => setError('')} aria-label="Schließen" data-testid="thread-fehler-schliessen"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}

      {locked ? (
        <div className="bg-rose-50 border-t-2 border-rose-400 p-4 text-sm" data-testid="chat-locked-banner">
          <div className="flex items-start gap-2">
            <Lock className="w-4 h-4 text-rose-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-rose-800 font-semibold mb-1">Chat derzeit gesperrt</p>
              <p className="text-rose-700 text-xs leading-relaxed">
                Die Kanzlei hat den Chat vorübergehend gesperrt. Bitte kontaktieren Sie uns telefonisch unter
                <strong> +49 (0) 228 30 40 640</strong> oder per E-Mail an{' '}
                <a href="mailto:bonn@kanzlei-partner.com" className="text-rose-800 underline">bonn@kanzlei-partner.com</a>.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="border-t border-gray-200 bg-white px-3 py-2.5" data-testid="composer">
          {isTeam && showEvent && (
            <div className="flex gap-2 mb-2 p-2 bg-[#fbf7f0] border-l-2 border-[#b8893d]" data-testid="event-form">
              <input value={eventText} onChange={(e) => setEventText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addEvent()}
                placeholder='Status-Update, z. B. "Schriftsatz beim BAMF eingereicht"' data-testid="event-input" autoFocus
                className="flex-1 px-3 py-1.5 text-sm bg-white border border-gray-200 focus:border-[#b8893d] focus:outline-none" />
              <button type="button" onClick={addEvent} disabled={!eventText.trim()} data-testid="event-submit-btn"
                className="px-3 py-1.5 text-[10px] tracking-wider bg-[#1B3A5C] text-white disabled:opacity-40">+ STATUS</button>
            </div>
          )}
          {file && (
            <div className="inline-flex items-center gap-2 mb-2 text-xs bg-[#f5f8fa] border border-gray-200 px-2.5 py-1 rounded-full text-[#1B3A5C]" data-testid="attachment-chip">
              <Paperclip className="w-3 h-3 text-[#b8893d]" /> <span className="truncate max-w-[220px]">{file.name}</span>
              <button type="button" onClick={() => setFile(null)} aria-label="Anhang entfernen" data-testid="attachment-remove"><X className="w-3 h-3" /></button>
            </div>
          )}
          {!allView && data.geteilt && <VertraulichComposer isTeam={isTeam} beteiligte={data.beteiligte || []} value={vertraulich} onChange={setVertraulich} />}
          {allView && data.threads.length > 1 && (
            <div className="flex items-center gap-2 mb-2 text-[11px] text-[#34536b]" data-testid="composer-target">
              <span className="tracking-wider">AN:</span>
              <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid="composer-target-select"
                className="bg-[#f5f8fa] border border-gray-200 px-2 py-1 text-[11px] font-mono text-[#1B3A5C] focus:outline-none focus:border-[#b8893d]">
                {data.threads.map((t) => <option key={t.case_id} value={t.case_id}>{t.is_general ? 'Allgemein' : `AZ ${t.label}`}</option>)}
              </select>
            </div>
          )}
          {isTeam && schnellantworten && extras && <div data-testid="composer-extras"><Schnellantworten onInsert={insertText} platzhalter={platzhalter} />
            {chatEnabled && <RechnungSenden mnr={platzhalter?.Mandantennummer || mandantenNr} onSend={send} />}</div>}
          <div className={`flex items-end gap-2 ${isTeam ? 'flex-wrap sm:flex-nowrap' : ''}`}>
            {isTeam && (
              <button type="button" onClick={() => setPlus((v) => !v)} aria-expanded={plus} aria-label="Weitere Funktionen" data-testid="composer-plus"
                className={`sm:hidden w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full transition-[background-color,transform] ${plus ? 'bg-[#1B3A5C] text-white rotate-45' : 'text-[#1B3A5C] bg-[#f5f8fa]'}`}>
                <Plus className="w-4 h-4" />
              </button>
            )}
            <label className={`w-9 h-9 flex-shrink-0 ${isTeam ? 'hidden sm:flex' : 'flex'} items-center justify-center rounded-full text-[#34536b] hover:bg-[#f5f8fa] cursor-pointer`} data-tipp="Anhang hinzufügen (max. 10 MB)">
              <Paperclip className="w-4 h-4" />
              <input type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="attachment-input" />
            </label>
            <textarea ref={taRef} rows={1} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onKeyDown}
              placeholder="Nachricht schreiben …" title="Enter = senden, Shift+Enter = Zeilenumbruch" data-testid="message-input"
              className="flex-1 min-w-0 w-0 bg-[#f5f8fa] rounded-2xl px-4 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-[#b8893d] leading-snug" />
            {isTeam && <div className={`${plus || aufnahme ? 'flex' : 'hidden'} sm:contents order-first basis-full items-center gap-1 pb-1 border-b border-gray-100 sm:border-0`} data-testid="composer-werkzeuge">
            <label className="sm:hidden w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full text-[#34536b] hover:bg-[#f5f8fa] cursor-pointer" aria-label="Anhang hinzufügen">
              <Paperclip className="w-4 h-4" />
              <input type="file" className="hidden" onChange={(e) => { setFile(e.target.files?.[0] || null); setPlus(false); }} data-testid="attachment-input-mobil" />
            </label>
            <QuickReplies onInsert={(t) => { insertText(t); setPlus(false); }} isAdmin={['admin', 'kanzlei_mitarbeiter'].includes(currentUser?.role)}
              platzhalter={{ ...platzhalter, ...(allView ? { Aktenzeichen: data.threads.find((t) => t.case_id === target && !t.is_general)?.aktenzeichen || '' } : {}) }} />
            {isTeam && schnellantworten && (
              <button type="button" onClick={() => setExtras((v) => !v)} data-testid="composer-extras-toggle" aria-pressed={extras} title={extras ? 'Schnellantworten und Rechnung ausblenden' : 'Schnellantworten und Rechnung einblenden'}
                className={`w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full transition-colors ${extras ? 'bg-[#1B3A5C] text-white' : 'text-[#b8893d] hover:bg-[#f5f8fa]'}`}>
                <Zap className="w-4 h-4" />
              </button>
            )}
            {isTeam && (
              <button type="button" onClick={() => setShowEvent((v) => !v)} data-testid="event-toggle-btn" title="Status-Update eintragen (nur Team)"
                className={`w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full transition-colors ${showEvent ? 'bg-[#1B3A5C] text-white' : 'text-[#34536b] hover:bg-[#f5f8fa]'}`}>
                <Clock className="w-4 h-4" />
              </button>
            )}
            {chatEnabled && <SprachAufnahme disabled={sending} onLaeuft={setAufnahme} onFertig={(f) => { setFile(f); setPlus(false); }} />}
            </div>}
            <button type="button" onClick={send} disabled={sending || (!text.trim() && !file)} data-testid="send-message-btn" aria-label="Senden"
              className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full bg-[#b8893d] hover:bg-[#a07730] text-white disabled:opacity-40 transition-colors">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </div>
      )}
      {moving && <MoveMessageDialog message={moving} onClose={() => setMoving(null)} onMoved={() => { setMoving(null); load(true); }} />}
      {freigabe && <NachrichtFreigabe text={freigabe.t} caseId={freigabe.target} mnr={mandantenNr || platzhalter?.Mandantennummer || (freigabe.target.startsWith('general_') ? freigabe.target.slice(8) : null)}
        onClose={() => setFreigabe(null)} onConfirm={(punkte, ziel) => sendenFertig(freigabe, punkte, ziel)} />}
      {preview && (
        <FilePreviewModal filename={preview.att.filename} contentType={preview.att.mime} url={preview.url} onClose={closePreview}
          onDownload={() => onDownload(preview.att, preview.cid)} testId="chat-attachment-preview" />
      )}
    </div>
  );
};

export default Thread;
